/**
 * Ticket conversation state (defect-first Phase 5; ported from mackey's
 * design — see docs/planning/DEFECT_FIRST_ISSUE_ANALYSIS_PLAN.md).
 *
 * Deliberately separate from `chatStore`: document chat and ticket chat are
 * different conversations against different backends, and mixing them would
 * put ticket turns into the document-chat history (and its persisted state).
 *
 * Rule 7: the model receives the analyst's QUESTIONS only — assistant
 * answers are generated text and never re-enter a generation stage; the
 * backend re-derives facts from the source tools. The seeded analysis
 * markdown is DISPLAY ONLY.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { askTicketChat, type ChatEvidence } from '../services/ticketChatApi';

export interface TicketTurn {
  role: 'user' | 'assistant';
  content: string;
  /**
   * Shown in the thread but NEVER sent back to the model. The seeded
   * analysis is generated prose — rule 7 allows showing it to a human,
   * not feeding it into a later model call.
   */
  localOnly?: boolean;
  /** Small caption above the message, e.g. the analysis heading. */
  label?: string;
  /** Lookups the backend ran for this answer. Assistant turns only. */
  evidence?: ChatEvidence[];
  elapsedMs?: number;
}

interface TicketChatState {
  ticketKey: string | null;
  /** Modules and entities for the ticket, passed through to SQL Investigation. */
  modules: string[];
  entities: unknown[];
  /** Where "close" returns to, so the ticket is not lost. */
  returnTo: { ticketKey: string; analysisId: number | null } | null;
  /** Height of the SQL panel, in vh. Persisted so it survives navigation. */
  sqlPanelVh: number;

  turns: TicketTurn[];
  isLoading: boolean;
  error: string | null;
  showSqlDrawer: boolean;

  open: (args: {
    ticketKey: string;
    modules?: string[];
    entities?: unknown[];
    analysisMarkdown?: string;
    analysisId?: number | null;
  }) => void;
  /** Resets view state only — the thread survives for a same-ticket
   *  re-open; navigation is the component's job, not the store's. */
  close: () => void;
  ask: (question: string) => Promise<void>;
  setSqlDrawer: (open: boolean) => void;
  setSqlPanelVh: (vh: number) => void;
  takeReturnTo: () => { ticketKey: string; analysisId: number | null } | null;
}

/** The analysis, as the opening message of the thread rather than a fixed
 *  band above it — it scrolls away like any other message. */
function analysisTurn(markdown: string): TicketTurn[] {
  if (!markdown.trim()) return [];
  return [
    {
      role: 'assistant',
      content: markdown,
      localOnly: true,
      label: 'Written analysis',
    },
  ];
}

/** A trailing user turn is a question that never got its answer (failed
 *  send, or a reload mid-flight) — drop it so it cannot dangle forever. */
function dropTrailingUser(turns: TicketTurn[]): TicketTurn[] {
  const last = turns[turns.length - 1];
  return last && last.role === 'user' && !last.localOnly ? turns.slice(0, -1) : turns;
}

export const useTicketChatStore = create<TicketChatState>()(
  persist(
    (set, get) => ({
      ticketKey: null,
      modules: [],
      entities: [],
      returnTo: null,
      sqlPanelVh: 42,
      turns: [],
      isLoading: false,
      error: null,
      showSqlDrawer: false,

      open: ({ ticketKey, modules, entities, analysisMarkdown, analysisId }) => {
        const current = get().ticketKey;
        // Re-opening the same ticket keeps the thread; a different ticket
        // starts a new one rather than mixing two tickets in one history.
        if (current === ticketKey) {
          set({
            modules: modules ?? get().modules,
            entities: entities ?? get().entities,
            returnTo: { ticketKey, analysisId: analysisId ?? null },
            turns: get().turns.length
              ? dropTrailingUser(get().turns)
              : analysisTurn(analysisMarkdown ?? ''),
            isLoading: false,
            error: null,
          });
          return;
        }
        set({
          ticketKey,
          modules: modules ?? [],
          entities: entities ?? [],
          returnTo: { ticketKey, analysisId: analysisId ?? null },
          turns: analysisTurn(analysisMarkdown ?? ''),
          isLoading: false,
          error: null,
          showSqlDrawer: false,
        });
      },

      close: () => {
        set({ isLoading: false, error: null, showSqlDrawer: false });
      },

      /** Consumed once by Issue Analysis to reselect the ticket. */
      takeReturnTo: () => {
        const r = get().returnTo;
        if (r) set({ returnTo: null });
        return r;
      },

      ask: async (question: string) => {
        const { ticketKey, turns, isLoading } = get();
        if (!ticketKey || !question.trim() || isLoading) return;

        // Rule 7: only the analyst's own earlier questions travel — answered
        // ones, newest last, capped to match the backend's window.
        const priorQuestions: string[] = [];
        let pendingUser: string | null = null;
        for (const t of turns) {
          if (t.localOnly) continue;
          if (t.role === 'user') {
            pendingUser = t.content;
          } else if (pendingUser !== null) {
            priorQuestions.push(pendingUser);
            pendingUser = null;
          }
        }

        set({
          turns: [...turns, { role: 'user', content: question }],
          isLoading: true,
          error: null,
        });

        try {
          const res = await askTicketChat(ticketKey, question, priorQuestions.slice(-10));
          // A late response must not land in another ticket's thread.
          if (get().ticketKey !== ticketKey) return;
          set((s) => ({
            turns: [
              ...s.turns,
              {
                role: 'assistant',
                content: res.answer,
                evidence: res.evidence,
                elapsedMs: res.elapsed_ms,
              },
            ],
            isLoading: false,
          }));
        } catch (e) {
          if (get().ticketKey !== ticketKey) return;
          // Drop the unanswered user turn back out of the thread so a retry
          // does not send the question twice.
          set((s) => ({
            turns: dropTrailingUser(s.turns),
            isLoading: false,
            error: e instanceof Error ? e.message : 'Request failed',
          }));
        }
      },

      setSqlDrawer: (open) => set({ showSqlDrawer: open }),
      setSqlPanelVh: (vh) => set({ sqlPanelVh: Math.min(80, Math.max(20, vh)) }),
    }),
    {
      name: 'lisa-ticket-chat',
      partialize: (state) => ({
        ticketKey: state.ticketKey,
        modules: state.modules,
        entities: state.entities,
        returnTo: state.returnTo,
        sqlPanelVh: state.sqlPanelVh,
        // Evidence payloads are diagnostics, not conversation — stripping
        // them keeps the localStorage write small.
        turns: state.turns.map(({ evidence: _evidence, ...t }) => t),
      }),
    },
  ),
);
